<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('receipt.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          { text: $t('receipt.pageTitle'), href: '/' },
          {
            text: $t('create'),
            active: true,
          },
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <a
            href="#"
            v-tooltip="$t('back')"
            @click.prevent="$router.push({ name: 'receipt' })"
          >
            <i class="fa fa-arrow-circle-left fa-2x"></i>
          </a>
          <ts-input
            v-model="search"
            :placeholder="$t('search')"
            style="width: 250px"
            class="inline-block"
          />
        </div>
        <ts-loading-banner :loading="loading">
          <table id="list" class="table mt-3">
            <thead>
              <tr>
                <th>{{ $t('receipt.invoiceNumber') }}</th>
                <th>{{ $t('receipt.studentId') }}</th>
                <th>{{ $t('receipt.studentName') }}</th>
                <th>{{ $t('receipt.sex') }}</th>
                <th>{{ $t('receipt.nextPaymentDate') }}</th>
                <th>{{ $t('receipt.amount') }}</th>
                <th>{{ $t('receipt.amountDue') }}</th>
                <th style="width: 30px">{{ $t('actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(resource, index) in resources" :key="index">
                <td>{{ resource.inv_number }}</td>
                <td>{{ is_external ? resource.ext_card_number : resource.card_number }}</td>
                <td>{{ resource.full_name_en }}</td>
                <td>{{ resource.gender }}</td>
                <td>{{ resource.next_payment_date }}</td>
                <td>{{ resource.inv_amount }}</td>
                <td>{{ resource.amount_due }}</td>
                <td class="text-center">
                  <a
                    href="#"
                    class="text-primary"
                    v-tooltip="$t('receipt.pay')"
                    @click.prevent="visitableModel(resource)"
                  >
                    <i class="fas fa-file-invoice-dollar"></i>
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </ts-loading-banner>
        <a-modal
          v-if="visible"
          v-model="visible"
          :title="$t('receipt.pay')"
          @cancel="cancelPayment"
          width="60%"
          :maskClosable="false"
          :confirmLoading="waiting"
          :footer="null"
          :zIndex="1020"
        >
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <form-component-v1 
                    v-model="model" 
                    :validate="errors"
                    :banks="banks"
                  >
                  <template v-slot:button-action>
                    <ts-button class="mr-2" @click.prevent="cancelPayment">{{
                      $t('cancel')
                    }}</ts-button>
                    <ts-button
                      :waiting="waiting"
                      color="primary"
                      @click.prevent="payReceipt"
                      >{{ $t('save') }}</ts-button
                    >
                  </template>
                </form-component-v1>
              </div>
            </div>
           
          </div>
          <div class="row">
             <div class="col-md-12">
              <div class="form-group">
                <preview-pdf-component :src="src" :waiting="waiting" />
              </div>
            </div>
          </div>
        </a-modal>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import FormComponentV1 from './comments/form_v1.vue'
import PreviewPdfComponent from './comments/pdf-preview'

export default {
  name: 'receiptCreate',
  components: {
    FormComponentV1,
    PreviewPdfComponent,
  },
  data() {
    return {
      waiting: false,
      loading: false,
      search: null,
      visible: false,
      src: null,
      resources: [],
      banks: [],
      errors: new Errors(),
      model: {
        inv_id: null,
        invoice_number: null,
        inv_amount: null,
        amount_paid: null,
        amount_due: 0,
        exchange_rate: null,
        payment_date: moment().format('DD-MM-YYYY'),
        next_payment_date: null,
        comment: null,
        bank_id: null,
        payment_source: 1, // -- 1 = Counter, 2 = Bank
        is_return: 1, // 1 is return
        payment_type: 2, // 2 cash,
        amount_paid_khr: 0,
        amount_paid_usd: 0,
        payment_method_id: 1, // cash
      },
    }
  },
  created() {
    this.getInvoiceList()
    this.getDefaultCreditDay()
    this.getExchangeRate()
  },
  computed: {
    ...mapState('receipt', ['exchangeRate']),
    is_external() {
      return this.$store.state.authUser.is_external
    },
  },
  methods: {
    ...mapActions('receipt', [
      'fetchInvoice',
      'store',
      'getDefaultCreditDay',
      'getExchangeRate',
      'formViewModel'
    ]),
    getInvoiceList(attributes = {}) {
      this.loading = true
      this.fetchInvoice(attributes)
        .then(async (response) => {
          this.resources = response.data
          await this.formViewModel({
            fnName: 'bank'
          })
          .then(({ data }) => {
            this.banks = data.bank
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    visitableModel(record) {
      this.errors = new Errors()
      this.visible = true
      this.model.inv_id = record.inv_id
      this.model.invoice_number = record.inv_number
      this.model.inv_amount = record.amount_due // set amount due the previous invoice to pay again
      this.model.exchange_rate = this.exchangeRate
      this.model.payment_date = moment().format('DD-MM-YYYY')
      this.model.next_payment_date = null
      this.model.comment = null
    },
    payReceipt() {
      this.waiting = true
      this.store(this.model)
        .then((response) => {
          this.$toasted.success(response.message)
          this.clearInput()
          this.src = response.url
        })
        .catch((error) => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    cancelPayment() {
      this.visible = false
      this.clearInput()
      this.getInvoiceList()
    },
    clearInput() {
      this.model.inv_id = null;
      this.model.invoice_number = null;
      this.model.inv_amount = null;
      this.model.amount_paid = null;
      this.model.amount_due = 0;
      this.model.exchange_rate = null;
      this.model.payment_date = moment().format('DD-MM-YYYY');
      this.model.next_payment_date = null;
      this.model.comment = null;
      this.model.bank_id = null;
      this.model.payment_source = 1; // -- 1 = Counter, 2 = Bank
      this.model.is_return = 1; // 1 is return
      this.model.payment_type = 2; // 2 cash
      this.model.amount_paid_khr = 0; // 2 cash
      this.model.payment_method_id = 1;
      this.src = null
    },
  },
  watch: {
    search: debounce(function (search) {
      this.getInvoiceList({ search: search })
    }, 500),
    is_external: debounce(function (val) {
      this.getInvoiceList({ search: this.search })
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('receipt/RESET_STATE')
    next()
  },
}
</script>

<style lang="css" scoped>
label {
  font-size: 12px !important;
}
#list > thead > tr > th {
  padding: 5px 4px;
  background: #f2f3f4;
  text-transform: uppercase;
}
#list > tbody > tr > td {
  padding: 5px 4px;
  border: none !important;
  font-size: 12px !important;
}
</style>
